import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { BasePostInput } from "@/components/common/BasePostInput";
import { useStudyGroupMentionAvailableUsers } from "@/hooks/useStudyGroupMentionAvailableUsers";
import type { User } from "@/lib/humhub-api/types";

interface CreatePostProps {
  studyGroupId?: number | null;
  onCreatePost: (message: string, files?: File[]) => Promise<void>;
  compact?: boolean;
  onCreated?: () => void;
  onError?: (error: unknown) => void;
  autoFocus?: boolean;
  searchLikeWhenBlurred?: boolean;
}

export const CreatePost: React.FC<CreatePostProps> = ({
  studyGroupId,
  onCreatePost,
  compact = false,
  onCreated,
  onError,
  autoFocus = false,
  searchLikeWhenBlurred = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: rawMentionUsers,
    isLoading: isLoadingMentionUsers,
    searchMentionAvailableUsers,
  } = useStudyGroupMentionAvailableUsers(studyGroupId || null);

  const mentionUsers = useMemo((): User[] => {
    return rawMentionUsers || [];
  }, [rawMentionUsers]);

  const handleMentionSearch = useCallback(
    async (query: string): Promise<User[]> => {
      try {
        const users = await searchMentionAvailableUsers(query);
        return users || [];
      } catch (error) {
        console.error("メンション検索中にエラーが発生しました:", error);
        return [];
      }
    },
    [searchMentionAvailableUsers],
  );

  const handleSubmit = async (content: string, files?: File[]) => {
    if (!content.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onCreatePost(content, files);
      onCreated?.();
    } catch (error) {
      onError?.(error);
      console.error("Failed to create post:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasePostInput
      onSubmit={handleSubmit}
      disabled={isSubmitting}
      mentionUsers={mentionUsers}
      isLoadingMentionUsers={isLoadingMentionUsers}
      onMentionTrigger={handleMentionSearch}
      compact={compact}
      autoFocus={autoFocus}
      searchLikeWhenBlurred={searchLikeWhenBlurred}
    />
  );
};
